import React, { useEffect, useState } from "react";
import {
  IndianRupee, Loader2, RefreshCw, Send, Mail, Eye, Ban, Search, Gift, Save, ShieldAlert,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../../lib/api";

const STATUS_TONE = {
  paid: "border-green-300 bg-green-50 text-green-800",
  created: "border-amber-300 bg-amber-50 text-amber-900",
  cancelled: "border-tara-teal/25 bg-white text-tara-muted",
  failed: "border-red-300 bg-red-50 text-red-800",
  signature_failed: "border-red-300 bg-red-50 text-red-800",
};

const Pill = ({ children, tone = "" }) => (
  <span className={`rounded-full border px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider ${tone || "border-tara-teal/25 bg-white text-tara-muted"}`}>
    {children}
  </span>
);

const DeliveryPill = ({ label, state }) => {
  const s = state?.status || "pending";
  const tone = s === "sent" ? "border-green-300 bg-green-50 text-green-800"
    : s === "failed" ? "border-amber-300 bg-amber-50 text-amber-900"
    : "border-tara-teal/20 bg-white text-tara-muted";
  return <Pill tone={tone}>{label}: {s}</Pill>;
};

export default function AstroPaymentsPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [pricing, setPricing] = useState(null);
  const [savingPrice, setSavingPrice] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [revealed, setRevealed] = useState({});
  const [grant, setGrant] = useState({ report_id: "", name: "", phone: "", email: "", note: "" });
  const [granting, setGranting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [list, price] = await Promise.all([
        api.get("/admin/astro/payments", { params: q ? { q } : {} }),
        api.get("/admin/astro/pricing"),
      ]);
      setData(list.data);
      setPricing(price.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const savePricing = async () => {
    setSavingPrice(true);
    try {
      const { data: saved } = await api.put("/admin/astro/pricing", {
        amount: Number(pricing.amount), label: pricing.label, description: pricing.description,
      });
      setPricing((p) => ({ ...p, ...saved }));
      toast.success(`Price saved — ${saved.amount_display}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally {
      setSavingPrice(false);
    }
  };

  const act = async (row, action, body) => {
    setBusyId(`${row.code_id}-${action}`);
    try {
      const { data: res } = await api.post(`/admin/astro/codes/${row.code_id}/${action}`, body || {});
      if (action === "reveal") {
        setRevealed((r) => ({ ...r, [row.code_id]: res.code }));
      } else if (action === "revoke") {
        toast.success("Code revoked and the report re-locked");
        load();
      } else {
        const wa = res.delivery?.whatsapp?.status, em = res.delivery?.email?.status;
        toast.success(`Resent — WhatsApp: ${wa || "n/a"}, email: ${em || "n/a"}`);
        load();
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally {
      setBusyId(null);
    }
  };

  const submitGrant = async () => {
    if (!grant.report_id.trim()) return toast.error("Paste the report ID to grant access to");
    setGranting(true);
    try {
      const { data: res } = await api.post("/admin/astro/grant", grant);
      if (res.already_granted) toast.info(`This report already has a code (${res.code_masked})`);
      else toast.success(`Code generated: ${res.code}`);
      setGrant({ report_id: "", name: "", phone: "", email: "", note: "" });
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally {
      setGranting(false);
    }
  };

  const rows = data?.items || [];
  const stats = data?.stats || {};
  const providers = data?.providers || {};

  return (
    <div className="space-y-6" data-testid="astro-payments-panel">
      {/* Provider status */}
      {(!providers.payments || !providers.whatsapp || !providers.email) && (
        <div className="rounded-2xl border border-amber-300/60 bg-amber-50/70 p-4" data-testid="provider-warning">
          <p className="flex items-center gap-2 text-sm font-semibold text-amber-900">
            <ShieldAlert className="h-4 w-4" /> Some providers are not configured yet
          </p>
          <p className="mt-1 text-xs text-amber-900/80">
            Razorpay: {providers.payments ? "ready" : "missing keys"} · WhatsApp Cloud API:{" "}
            {providers.whatsapp ? "ready" : "missing keys"} · Email: {providers.email ? "ready" : "missing keys"}.
            Unlock codes still work — deliveries stay pending until the keys are added, and you can resend from here.
          </p>
        </div>
      )}

      {/* Stats */}
      <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-6">
        {[
          ["Revenue", stats.revenue_display || "₹0"],
          ["Paid", stats.paid ?? 0],
          ["Pending", stats.pending ?? 0],
          ["Failed", stats.failed ?? 0],
          ["Unlocked", stats.unlocked ?? 0],
          ["Delivery issues", stats.delivery_issues ?? 0],
        ].map(([label, value]) => (
          <div key={label} className="glass rounded-2xl p-4" data-testid={`astro-stat-${label.toLowerCase().replace(/\s/g, "-")}`}>
            <p className="text-[10px] uppercase tracking-[0.16em] text-tara-muted">{label}</p>
            <p className="font-display text-2xl text-tara-ink">{value}</p>
          </div>
        ))}
      </div>

      {/* Pricing */}
      {pricing && (
        <div className="glass rounded-2xl p-5" data-testid="astro-pricing-editor">
          <h3 className="mb-4 flex items-center gap-2 font-serif text-sm uppercase tracking-[0.18em] text-tara-tealDeep">
            <IndianRupee className="h-3.5 w-3.5" /> Report price & label
          </h3>
          <div className="grid gap-3 sm:grid-cols-[140px_1fr] lg:grid-cols-[140px_1fr_2fr]">
            <div>
              <label className="mb-1 block text-[10px] uppercase tracking-[0.14em] text-tara-muted">Amount (paise)</label>
              <input type="number" value={pricing.amount}
                     onChange={(e) => setPricing({ ...pricing, amount: e.target.value })}
                     className="w-full rounded-xl border border-tara-teal/25 bg-white/85 px-3 py-2.5 text-sm outline-none focus:border-tara-teal/60"
                     data-testid="pricing-amount-input" />
              <p className="mt-1 text-[10px] text-tara-muted">= {pricing.amount_display}</p>
            </div>
            <div>
              <label className="mb-1 block text-[10px] uppercase tracking-[0.14em] text-tara-muted">Product label</label>
              <input value={pricing.label} onChange={(e) => setPricing({ ...pricing, label: e.target.value })}
                     className="w-full rounded-xl border border-tara-teal/25 bg-white/85 px-3 py-2.5 text-sm outline-none focus:border-tara-teal/60"
                     data-testid="pricing-label-input" />
            </div>
            <div>
              <label className="mb-1 block text-[10px] uppercase tracking-[0.14em] text-tara-muted">Description shown to devotees</label>
              <input value={pricing.description} onChange={(e) => setPricing({ ...pricing, description: e.target.value })}
                     className="w-full rounded-xl border border-tara-teal/25 bg-white/85 px-3 py-2.5 text-sm outline-none focus:border-tara-teal/60"
                     data-testid="pricing-description-input" />
            </div>
          </div>
          <button onClick={savePricing} disabled={savingPrice}
                  className="btn-sacred mt-4 inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-serif font-semibold"
                  data-testid="pricing-save-btn">
            {savingPrice ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save price
          </button>
        </div>
      )}

      {/* Manual grant */}
      <div className="glass rounded-2xl p-5" data-testid="astro-grant-form">
        <h3 className="mb-3 flex items-center gap-2 font-serif text-sm uppercase tracking-[0.18em] text-tara-tealDeep">
          <Gift className="h-3.5 w-3.5" /> Issue a code manually (offline payment / fix an issue)
        </h3>
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-5">
          {[["report_id", "Report ID"], ["name", "Name"], ["phone", "WhatsApp number"], ["email", "Email"], ["note", "Internal note"]].map(([k, ph]) => (
            <input key={k} value={grant[k]} onChange={(e) => setGrant({ ...grant, [k]: e.target.value })}
                   placeholder={ph}
                   className="rounded-xl border border-tara-teal/25 bg-white/85 px-3 py-2.5 text-sm outline-none focus:border-tara-teal/60"
                   data-testid={`grant-${k}-input`} />
          ))}
        </div>
        <button onClick={submitGrant} disabled={granting}
                className="btn-ghost-gold mt-3 inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-serif font-semibold"
                data-testid="grant-submit-btn">
          {granting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Gift className="h-4 w-4" />} Generate & send code
        </button>
      </div>

      {/* Search + list */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-tara-muted" />
          <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()}
                 placeholder="Search by name, email, report ref, payment ID…"
                 className="w-full rounded-full border border-tara-teal/25 bg-white/85 pl-10 pr-4 py-2.5 text-sm outline-none focus:border-tara-teal/60"
                 data-testid="astro-payments-search" />
        </div>
        <button onClick={load} className="btn-ghost-gold inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-serif font-semibold" data-testid="astro-payments-refresh">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Refresh
        </button>
      </div>

      {rows.length === 0 && !loading && (
        <p className="glass rounded-2xl p-8 text-center text-sm text-tara-muted" data-testid="astro-payments-empty">
          No astrology payments yet.
        </p>
      )}

      <div className="space-y-3">
        {rows.map((row) => (
          <div key={row.order_ref} className="glass rounded-2xl p-5" data-testid={`payment-row-${row.order_ref}`}>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <p className="font-display text-lg text-tara-ink">{row.name || "—"}</p>
                <p className="text-xs text-tara-muted">
                  {row.email_masked} · {row.phone_masked} · <span className="font-mono">{row.report_ref}</span>
                </p>
                <p className="mt-1 text-[11px] font-mono text-tara-muted">
                  order {row.order_ref}{row.payment_id ? ` · payment ${row.payment_id}` : ""}
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Pill tone={STATUS_TONE[row.status]}>{row.status}</Pill>
                <Pill>{row.amount_display}</Pill>
                {row.gateway === "manual" && <Pill>manual</Pill>}
                {row.code_status && <Pill tone={row.code_status === "revoked" ? "border-red-300 bg-red-50 text-red-800" : "border-tara-teal/30 bg-tara-teal/[0.07] text-tara-tealDeep"}>code {row.code_status}</Pill>}
              </div>
            </div>

            <div className="mt-3 flex flex-wrap items-center gap-2 text-[11px] text-tara-muted">
              <span className="font-mono">{revealed[row.code_id] || row.code_masked || "—"}</span>
              {row.code_created_at && <span>· created {row.code_created_at.slice(0, 16).replace("T", " ")}</span>}
              {row.unlocked_at && <span>· unlocked {row.unlocked_at.slice(0, 16).replace("T", " ")}</span>}
              {row.failure_reason && <span className="text-amber-800">· {row.failure_reason}</span>}
            </div>

            {row.code_id && (
              <>
                <div className="mt-3 flex flex-wrap gap-2">
                  <DeliveryPill label="WhatsApp" state={row.delivery?.whatsapp} />
                  <DeliveryPill label="Email" state={row.delivery?.email} />
                  {row.delivery?.whatsapp?.detail && row.delivery.whatsapp.status !== "sent" && (
                    <span className="text-[10px] text-amber-800">{row.delivery.whatsapp.detail}</span>
                  )}
                  {row.delivery?.email?.detail && row.delivery.email.status !== "sent" && (
                    <span className="text-[10px] text-amber-800">{row.delivery.email.detail}</span>
                  )}
                </div>
                <div className="mt-4 flex flex-wrap gap-2">
                  <button onClick={() => act(row, "resend", { channel: "whatsapp" })} disabled={busyId === `${row.code_id}-resend`}
                          className="inline-flex items-center gap-1.5 rounded-full border border-tara-teal/25 bg-white px-4 py-2 text-[11px] font-semibold hover:border-tara-teal/60"
                          data-testid={`resend-wa-${row.order_ref}`}>
                    <Send className="h-3 w-3" /> Resend on WhatsApp
                  </button>
                  <button onClick={() => act(row, "resend", { channel: "email" })} disabled={busyId === `${row.code_id}-resend`}
                          className="inline-flex items-center gap-1.5 rounded-full border border-tara-teal/25 bg-white px-4 py-2 text-[11px] font-semibold hover:border-tara-teal/60"
                          data-testid={`resend-email-${row.order_ref}`}>
                    <Mail className="h-3 w-3" /> Resend email
                  </button>
                  <button onClick={() => act(row, "reveal")}
                          className="inline-flex items-center gap-1.5 rounded-full border border-tara-teal/25 bg-white px-4 py-2 text-[11px] font-semibold hover:border-tara-teal/60"
                          data-testid={`reveal-${row.order_ref}`}>
                    <Eye className="h-3 w-3" /> Reveal code
                  </button>
                  {row.code_status === "active" && (
                    <button onClick={() => window.confirm("Revoke this code and re-lock the report?") && act(row, "revoke")}
                            className="inline-flex items-center gap-1.5 rounded-full border border-red-300 bg-red-50 px-4 py-2 text-[11px] font-semibold text-red-800 hover:bg-red-100"
                            data-testid={`revoke-${row.order_ref}`}>
                      <Ban className="h-3 w-3" /> Revoke
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
